import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ExpandIcon } from '../../../../common/Icons';
import colors from '../../../../../scss/colors';
import classes from './ScrollableSlider.module.scss';

// ─── Constants ───────────────────────────────────────────────────────────────
const ARROW_SCROLL_PX = 80;
const THUMB_MIN_WIDTH_PX = 48;

// ─── Helpers ─────────────────────────────────────────────────────────────────
const getMaxScroll = (el) => (el ? el.scrollWidth - el.clientWidth : 0);

const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

// ─── Component ───────────────────────────────────────────────────────────────
export const ScrollableSlider = ({
  scroll,
  setScroll,
  scrollLeftRight,
  submissionType,
  submissionEnum,
  scrollableRef,
  scrollablePerDiem,
}) => {
  const trackRef = useRef(null);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartScroll = useRef(0);

  // ── Thumb geometry ──────────────────────────────────────────────────────
  const [thumbGeometry, setThumbGeometry] = useState({ width: THUMB_MIN_WIDTH_PX, left: 0 });

  const computeThumb = useCallback(() => {
    const el = scrollableRef?.current;
    const track = trackRef.current;
    if (!el || !track) return;

    const trackWidth = track.clientWidth;
    const ratio = el.clientWidth / el.scrollWidth; // visible ÷ total
    const thumbWidth = Math.max(THUMB_MIN_WIDTH_PX, ratio * trackWidth);
    const maxThumbLeft = trackWidth - thumbWidth;
    const maxScroll = getMaxScroll(el);
    const thumbLeft = maxScroll > 0 ? (el.scrollLeft / maxScroll) * maxThumbLeft : 0;

    setThumbGeometry({ width: thumbWidth, left: thumbLeft });
  }, [scrollableRef]);

  // ── Sync from native scroll events ──────────────────────────────────────
  useEffect(() => {
    const el = scrollableRef?.current;
    if (!el) return;

    const onScroll = () => {
      const maxScroll = getMaxScroll(el);
      const pct = maxScroll > 0 ? Math.round((el.scrollLeft / maxScroll) * 100) : 0;
      setScroll(pct);

      if (scrollablePerDiem?.current) {
        scrollablePerDiem.current.scrollLeft = el.scrollLeft;
      }
      computeThumb();
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [scrollableRef, scrollablePerDiem, setScroll, computeThumb]);

  // ── Recompute thumb on resize ────────────────────────────────────────────
  useEffect(() => {
    const el = scrollableRef?.current;
    if (!el) return;
    const ro = new ResizeObserver(computeThumb);
    ro.observe(el);
    computeThumb(); // initial
    return () => ro.disconnect();
  }, [scrollableRef, computeThumb]);

  // ── Pointer-based drag ───────────────────────────────────────────────────
  const syncScrollFromThumbLeft = useCallback(
    (thumbLeft) => {
      const el = scrollableRef?.current;
      const track = trackRef.current;
      if (!el || !track) return;

      const { width: thumbWidth } = thumbGeometry;
      const maxThumbLeft = track.clientWidth - thumbWidth;
      const ratio = maxThumbLeft > 0 ? clamp(thumbLeft, 0, maxThumbLeft) / maxThumbLeft : 0;
      const maxScroll = getMaxScroll(el);
      el.scrollLeft = ratio * maxScroll;

      if (scrollablePerDiem?.current) {
        scrollablePerDiem.current.scrollLeft = el.scrollLeft;
      }
    },
    [scrollableRef, scrollablePerDiem, thumbGeometry]
  );

  const onThumbPointerDown = useCallback(
    (e) => {
      e.preventDefault();
      isDragging.current = true;
      dragStartX.current = e.clientX;
      dragStartScroll.current = thumbGeometry.left;
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [thumbGeometry.left]
  );

  const onThumbPointerMove = useCallback(
    (e) => {
      if (!isDragging.current) return;
      const delta = e.clientX - dragStartX.current;
      syncScrollFromThumbLeft(dragStartScroll.current + delta);
    },
    [syncScrollFromThumbLeft]
  );

  const onThumbPointerUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  // ── Click on track (not thumb) to jump ──────────────────────────────────
  const onTrackClick = useCallback(
    (e) => {
      if (e.target !== trackRef.current) return; // only the track itself
      const rect = trackRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const { width: thumbWidth } = thumbGeometry;
      // Centre thumb on click position
      syncScrollFromThumbLeft(clickX - thumbWidth / 2);
    },
    [thumbGeometry, syncScrollFromThumbLeft]
  );

  // ── Arrow buttons ────────────────────────────────────────────────────────
  const arrowLeft = () => {
    const el = scrollableRef?.current;
    if (!el) return;
    el.scrollLeft -= ARROW_SCROLL_PX;
  };

  const arrowRight = () => {
    const el = scrollableRef?.current;
    if (!el) return;
    el.scrollLeft += ARROW_SCROLL_PX;
  };

  const atStart = scroll <= 0;
  const atEnd = scroll >= 100;

  return (
    <div className={classes.sliderContainer}>
      {/* Left arrow */}
      <button
        aria-label="scrollLeft"
        className={`${classes.arrowBtn} ${atStart ? classes.arrowDisabled : ''}`}
        onClick={arrowLeft}
        disabled={atStart}
      >
        <ExpandIcon rotate="90deg" />
      </button>

      {/* Custom track */}
      <div
        ref={trackRef}
        className={classes.customTrack}
        onClick={onTrackClick}
      >
        <div
          className={classes.customThumb}
          style={{
            width: `${thumbGeometry.width}px`,
            transform: `translateX(${thumbGeometry.left}px)`,
          }}
          onPointerDown={onThumbPointerDown}
          onPointerMove={onThumbPointerMove}
          onPointerUp={onThumbPointerUp}
          onPointerCancel={onThumbPointerUp}
        />
      </div>

      {/* Right arrow */}
      <button
        aria-label="scrollRight"
        className={`${classes.arrowBtn} ${atEnd ? classes.arrowDisabled : ''}`}
        onClick={arrowRight}
        disabled={atEnd}
      >
        <ExpandIcon rotate="-90deg" />
      </button>
    </div>
  );
};